// src/pages/Breeding.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import QuienesSomos from './quienes_somos';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin,
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  ChevronLeft,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';

const Breeding = ({ onChangeLanguage }) => {
  const { t, i18n } = useTranslation();
  const [temaSeleccionado, setTemaSeleccionado] = useState('tema1');
  const [indiceCarrusel, setIndiceCarrusel] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [menuAbierto, setMenuAbierto] = useState(false);

  // Configuración de temas con clases de Tailwind
  const temas = {
    tema1: {
      bg: 'bg-white',
      text: 'text-gray-800',
      card: 'bg-blue-50',
      cardHover: 'hover:bg-blue-100',
      border: 'border-blue-200',
      gradient: 'from-blue-500 to-blue-700',
      accent: 'text-blue-600',
      accentBg: 'bg-blue-600',
      accentHover: 'hover:bg-blue-700',
      selectBg: 'bg-blue-50',
      selectBorder: 'border-blue-300',
      heroGradient: 'from-blue-600 to-blue-900',
      badge: 'bg-blue-100 text-blue-800',
    },
    tema2: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-900',
      card: 'bg-emerald-100',
      cardHover: 'hover:bg-emerald-200',
      border: 'border-emerald-200',
      gradient: 'from-emerald-500 to-emerald-700',
      accent: 'text-emerald-600',
      accentBg: 'bg-emerald-600',
      accentHover: 'hover:bg-emerald-700',
      selectBg: 'bg-emerald-50',
      selectBorder: 'border-emerald-300',
      heroGradient: 'from-emerald-600 to-emerald-900',
      badge: 'bg-emerald-100 text-emerald-800',
    },
    tema3: {
      bg: 'bg-red-50',
      text: 'text-red-900',
      card: 'bg-red-100',
      cardHover: 'hover:bg-red-200',
      border: 'border-red-200',
      gradient: 'from-red-500 to-red-700',
      accent: 'text-red-600',
      accentBg: 'bg-red-600',
      accentHover: 'hover:bg-red-700',
      selectBg: 'bg-red-50',
      selectBorder: 'border-red-300',
      heroGradient: 'from-red-600 to-red-900',
      badge: 'bg-red-100 text-red-800',
    },
    tema4: {
      bg: 'bg-purple-50',
      text: 'text-purple-900',
      card: 'bg-purple-100',
      cardHover: 'hover:bg-purple-200',
      border: 'border-purple-200',
      gradient: 'from-purple-500 to-purple-700',
      accent: 'text-purple-600',
      accentBg: 'bg-purple-600',
      accentHover: 'hover:bg-purple-700',
      selectBg: 'bg-purple-50',
      selectBorder: 'border-purple-300',
      heroGradient: 'from-purple-600 to-purple-900',
      badge: 'bg-purple-100 text-purple-800',
    }
  };

  const temaActual = temas[temaSeleccionado];

  const urlsImagenesCarrusel = [
    'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1200&h=600&fit=crop&q=80',
    'https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=1200&h=600&fit=crop&q=80',
    'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&h=600&fit=crop&q=80'
  ];
  const imagenesCarrusel = t('projects.slides', { returnObjects: true }).map((slide, index) => ({
    ...slide,
    url: urlsImagenesCarrusel[index]
  }));

  // Función de scroll suave
  const scrollToSection = (sectionId) => {
    setMenuAbierto(false);
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 150);
  };

  // Auto-play del carrusel
  useEffect(() => {
    const interval = setInterval(() => {
      setIndiceCarrusel((prev) => (prev + 1) % imagenesCarrusel.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Efecto parallax
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!imagenesCarrusel || imagenesCarrusel.length === 0) {
    return <div className="text-center p-8">{t('loading.images')}</div>;
  }

  return (
    <div className={`min-h-screen ${temaActual.bg} ${temaActual.text}`}>
      
      {/* HEADER CON MENÚ */}
      <header className={`fixed top-0 left-0 right-0 z-50 ${temaActual.bg} backdrop-blur-md shadow-lg border-b ${temaActual.border}`}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => scrollToSection('inicio')}
          >
            <img
              src="/logo.png"
              alt="Logo de Cropsbreed SA"
              className="h-12 w-12 object-contain"
            />
            <h1 className="brand-wordmark text-2xl text-black">
              <span className="font-bold">CROPSBREED</span>{' '}
              <span className="font-normal">SA</span>
            </h1>
          </div>
          
          {/* Menú Desktop */}
          <nav className="hidden md:flex items-center gap-6">
            <button 
              onClick={() => scrollToSection('proyectos')}
              className="text-gray-600 hover:text-blue-600 transition-colors font-medium text-sm"
            >
              {t('navigation.projects')}
            </button>
            <button 
              onClick={() => scrollToSection('que-hacemos')}
              className="text-gray-600 hover:text-blue-600 transition-colors font-medium text-sm"
            >
              {t('navigation.services')}
            </button>
            <button 
              onClick={() => scrollToSection('historia')}
              className="text-gray-600 hover:text-blue-600 transition-colors font-medium text-sm"
            >
              {t('navigation.history')}
            </button>
            <button 
              onClick={() => scrollToSection('contacto')}
              className="text-gray-600 hover:text-blue-600 transition-colors font-medium text-sm"
            >
              {t('navigation.contact')}
            </button>
          </nav>

          <div role="group" aria-label={t('language.label')} className="flex items-center rounded-md border border-gray-300 p-1">
            <button
              type="button"
              onClick={() => onChangeLanguage('es')}
              aria-label={t('language.spanish')}
              aria-pressed={i18n.language === 'es'}
              title={t('language.spanish')}
              className={`px-2 py-1 rounded text-xs font-bold transition-colors ${i18n.language === 'es' ? `${temaActual.accentBg} text-white` : 'text-gray-600 hover:bg-gray-100'}`}
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => onChangeLanguage('en')}
              aria-label={t('language.english')}
              aria-pressed={i18n.language === 'en'}
              title={t('language.english')}
              className={`px-2 py-1 rounded text-xs font-bold transition-colors ${i18n.language === 'en' ? `${temaActual.accentBg} text-white` : 'text-gray-600 hover:bg-gray-100'}`}
            >
              EN
            </button>
          </div>

          {/* Botón Menú Mobile */}
          <button 
            onClick={() => setMenuAbierto(!menuAbierto)}
            aria-label={menuAbierto ? t('navigation.closeMenu') : t('navigation.openMenu')}
            aria-expanded={menuAbierto}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {menuAbierto ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Menú Mobile Desplegable */}
        <AnimatePresence>
          {menuAbierto && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`md:hidden ${temaActual.bg} border-t ${temaActual.border} overflow-hidden`}
            >
              <div className="container mx-auto px-4 py-4 flex flex-col gap-3">
                <button 
                  onClick={() => scrollToSection('proyectos')}
                  className="text-gray-600 hover:text-blue-600 transition-colors font-medium text-sm py-2 border-b border-gray-100 text-left w-full"
                >
                  {t('navigation.projects')}
                </button>
                <button 
                  onClick={() => scrollToSection('que-hacemos')}
                  className="text-gray-600 hover:text-blue-600 transition-colors font-medium text-sm py-2 border-b border-gray-100 text-left w-full"
                >
                  {t('navigation.services')}
                </button>
                <button 
                  onClick={() => scrollToSection('historia')}
                  className="text-gray-600 hover:text-blue-600 transition-colors font-medium text-sm py-2 border-b border-gray-100 text-left w-full"
                >
                  {t('navigation.history')}
                </button>
                <button 
                  onClick={() => scrollToSection('contacto')}
                  className="text-gray-600 hover:text-blue-600 transition-colors font-medium text-sm py-2 border-b border-gray-100 text-left w-full"
                >
                  {t('navigation.contact')}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* HERO CON PARALLAX */}
      <div id="inicio" className={`relative h-screen overflow-hidden pt-16 bg-gradient-to-r ${temaActual.heroGradient}`}>
        <motion.div 
          className="absolute inset-0"
          style={{
            y: scrollY * 0.5,
          }}
        >
          <div 
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1920&h=1080&fit=crop&q=80)',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          </div>
        </motion.div>
        
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center">
          <div className="max-w-2xl text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              {t('hero.titleLine1')}
              <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                {t('hero.titleLine2')}
              </span>
            </h1>
            <p className="text-xl mb-8 text-gray-200">
              {t('hero.description')}
            </p>
            <div className="flex flex-wrap gap-4">
              <button className={`px-8 py-4 bg-gradient-to-r ${temaActual.gradient} rounded-lg font-semibold shadow-xl hover:shadow-2xl transition-all`}>
                {t('hero.discover')}
              </button>
              <button className="px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white rounded-lg font-semibold hover:bg-white/30 transition-all">
                {t('hero.seeProjects')}
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center animate-bounce">
            <div className="w-1 h-3 bg-white rounded-full mt-2" />
          </div>
        </div>
      </div>

      {/* CARRUSEL */}
      <section id="proyectos" className="py-20 px-4">
        <div className="container mx-auto">
          <h2 className={`text-4xl font-bold text-center mb-12 bg-gradient-to-r ${temaActual.gradient} bg-clip-text text-transparent`}>
            {t('projects.title')}
          </h2>

          <div className="relative max-w-5xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={indiceCarrusel}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl"
              >
                <img 
                  src={imagenesCarrusel[indiceCarrusel].url} 
                  alt={imagenesCarrusel[indiceCarrusel].title}
                  className="w-full h-[400px] md:h-[500px] object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/1200x600/2563eb/ffffff?text=Cropsbreed+SA';
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-3 ${temaActual.badge}`}>
                    {t('projects.featured')}
                  </span>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    {imagenesCarrusel[indiceCarrusel].title}
                  </h3>
                  <p className="text-gray-200 text-lg">
                    {imagenesCarrusel[indiceCarrusel].desc}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Controles */}
            <button
              aria-label={t('projects.previous')}
              onClick={() => setIndiceCarrusel((prev) => (prev - 1 + imagenesCarrusel.length) % imagenesCarrusel.length)}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-md text-white p-3 rounded-full hover:bg-white/40 transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              aria-label={t('projects.next')}
              onClick={() => setIndiceCarrusel((prev) => (prev + 1) % imagenesCarrusel.length)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-md text-white p-3 rounded-full hover:bg-white/40 transition-all"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Indicadores */}
            <div className="flex justify-center gap-3 mt-6">
              {imagenesCarrusel.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setIndiceCarrusel(index)}
                  aria-label={t('projects.slideLabel', { number: index + 1 })}
                  aria-current={index === indiceCarrusel ? 'true' : undefined}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === indiceCarrusel ? 'bg-blue-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <QuienesSomos />

      {/* CONTACTO Y MAPA */}
      <section id="contacto" className={`py-12 px-4 bg-gray-900 text-white`}>
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Información de Contacto */}
            <div>
              <h2 className="text-2xl font-bold mb-6">{t('contact.title')}</h2>
              <div className="space-y-4">
                {[
                  { icon: MapPin, text: t('contact.address') },
                  { icon: Phone, text: t('contact.phone') },
                  { icon: Mail, text: t('contact.email') },
                  { icon: Clock, text: t('contact.hours') }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4 bg-white/10 rounded-lg p-3 backdrop-blur-sm hover:bg-white/20 transition-all">
                    <item.icon className="w-5 h-5 text-blue-400" />
                    <span className="text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <button className={`mt-6 px-6 py-3 bg-gradient-to-r ${temaActual.gradient} rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all text-sm`}>
                {t('contact.button')}
              </button>
            </div>

            {/* Mapa */}
            <div className="bg-gray-800 rounded-2xl p-4">
  <h3 className="text-xl font-bold mb-3">{t('contact.location')}</h3>
  <div className="bg-gray-700 rounded-xl h-[250px] overflow-hidden">
    {/* ⬇️ REEMPLAZA EL DIV GRIS CON ESTE IFRAME ⬇️ */}
    <iframe
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d18734.477352175727!2d-60.58347347129596!3d-33.89431523855412!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95b84aad3ef60a31%3A0x481ac3277962e9c5!2sPergamino%2C%20Provincia%20de%20Buenos%20Aires!5e0!3m2!1ses-419!2sar!4v1787086740457!5m2!1ses-419!2sar"
      width="100%"
      height="100%"
      style={{ border: 0 }}
      allowFullScreen=""
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      title={t('contact.mapTitle')}
    />
  </div>
</div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-950 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="brand-wordmark mb-4 text-2xl text-white">
                <span className="font-bold">CROPSBREED</span>{' '}
                <span className="font-normal">SA</span>
              </h3>
              <p className="text-gray-400 text-sm">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('footer.navigation')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('inicio')} className="text-gray-400 hover:text-white transition-colors">{t('footer.home')}</button></li>
                <li><button onClick={() => scrollToSection('proyectos')} className="text-gray-400 hover:text-white transition-colors">{t('footer.projects')}</button></li>
                <li><button onClick={() => scrollToSection('que-hacemos')} className="text-gray-400 hover:text-white transition-colors">{t('navigation.services')}</button></li>
                <li><button onClick={() => scrollToSection('contacto')} className="text-gray-400 hover:text-white transition-colors">{t('navigation.contact')}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('footer.contactQuick')}</h4>
              <p className="text-gray-400 text-sm flex items-center gap-2">
                <Phone className="w-4 h-4" /> +54 11 1234-5678
              </p>
              <p className="text-gray-400 text-sm flex items-center gap-2 mt-2">
                <Mail className="w-4 h-4" /> info@Breeding Digital.com.ar
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('footer.social')}</h4>
              <div className="flex gap-4">
                <a 
                  href="#" 
                  aria-label={t('footer.facebook')}
                  className="text-gray-400 hover:text-blue-400 transition-colors p-2 bg-gray-800 rounded-full hover:bg-gray-700"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href="#" 
                  aria-label={t('footer.twitter')}
                  className="text-gray-400 hover:text-blue-400 transition-colors p-2 bg-gray-800 rounded-full hover:bg-gray-700"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a 
                  href="#" 
                  aria-label={t('footer.instagram')}
                  className="text-gray-400 hover:text-pink-400 transition-colors p-2 bg-gray-800 rounded-full hover:bg-gray-700"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href="#" 
                  aria-label={t('footer.linkedin')}
                  className="text-gray-400 hover:text-blue-500 transition-colors p-2 bg-gray-800 rounded-full hover:bg-gray-700"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-gray-400 text-sm">
            © {new Date().getFullYear()} Cropsbreed SA {t('footer.rights')}
          </div>
        </div>
      </footer>

      {/* BOTÓN WHATSAPP */}
      <a
        href={`https://wa.me/5492477551246?text=${encodeURIComponent(t('whatsapp.message'))}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('whatsapp.label')}
        className="fixed bottom-8 right-8 z-50 bg-green-500 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform hover:shadow-green-500/50"
      >
        <Send className="w-6 h-6 text-white" />
      </a>
    </div>
  );
};

export default Breeding;